import React, { FC, ReactNode, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import useQueryParams from 'src/hooks/use-query-params';
import { getLSValue } from 'src/utils/locale-storage';
import { UserLocalStorageData } from 'src/types/user';
import { USER_DATA } from 'src/constants/common';

import { getAdsData } from './helpers';
import { useUserTrack } from './hooks';

type Props = {
  children: ReactNode;
};

const UserTrackingMiddleware: FC<Props> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);

  const { getQueryParams, updateQueryParams } = useQueryParams();
  const { trackNewUser, trackCurrentUser } = useUserTrack();

  useEffect(() => {
    (async () => {
      try {
        const userLSData = getLSValue(USER_DATA, true) as UserLocalStorageData;
        const queryParams = getQueryParams();

        const adsData: Record<string, string> = await getAdsData(queryParams);

        const isUserIdentified = isEqual(userLSData?.ads_data || {}, adsData) && userLSData?.user_id;

        if (isUserIdentified) {
          if (!queryParams?.customer_id) {
            updateQueryParams({ customer_id: userLSData.user_id });
          }
          return;
        }

        const isUserUntracked = queryParams?.customer_id && typeof queryParams.customer_id === 'string';

        if (isUserUntracked) {
          try {
            await trackCurrentUser(String(queryParams.customer_id), adsData);
          } catch (e: unknown) {
            console.error('Error getting customer:', e);
            await trackNewUser(adsData);
          }

          return;
        }

        await trackNewUser(adsData);
      } catch (e: unknown) {
        console.error('Failed to track user', e);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
};

export { UserTrackingMiddleware };
